// Тонка обгортка над window.Telegram.WebApp.
// Виносимо тут усі звернення до SDK — щоб у компонентах був тільки бізнес-код.

type TgThemeParams = {
  bg_color?: string;
  secondary_bg_color?: string;
  text_color?: string;
  hint_color?: string;
  link_color?: string;
  button_color?: string;
  button_text_color?: string;
  destructive_text_color?: string;
};

type TgWebApp = {
  initData: string;
  initDataUnsafe: {
    user?: { id: number; first_name: string; username?: string; photo_url?: string };
    // Передається в URL як ?startapp=... або в /start <param> при відкритті з deep-link.
    start_param?: string;
  };
  colorScheme: 'light' | 'dark';
  themeParams: TgThemeParams;
  viewportHeight: number;
  viewportStableHeight: number;
  isExpanded: boolean;
  ready: () => void;
  expand: () => void;
  close: () => void;
  HapticFeedback: {
    impactOccurred: (style: 'light' | 'medium' | 'heavy' | 'rigid' | 'soft') => void;
    notificationOccurred: (type: 'error' | 'success' | 'warning') => void;
    selectionChanged: () => void;
  };
  BackButton: { show: () => void; hide: () => void; onClick: (cb: () => void) => void; offClick: (cb: () => void) => void };
  MainButton: {
    text: string;
    show: () => void;
    hide: () => void;
    enable: () => void;
    disable: () => void;
    setText: (t: string) => void;
    onClick: (cb: () => void) => void;
    offClick: (cb: () => void) => void;
    showProgress: (leaveActive?: boolean) => void;
    hideProgress: () => void;
  };
  onEvent: (event: string, cb: () => void) => void;
  offEvent: (event: string, cb: () => void) => void;
  // Відкриває URL у зовнішньому браузері (а не в WebView Mini App'у).
  // Підтримується з Bot API 6.1+. Параметр try_instant_view не використовуємо.
  openLink?: (url: string, options?: { try_instant_view?: boolean }) => void;
  // Версія API для feature detection
  version?: string;
};

declare global {
  interface Window {
    Telegram?: { WebApp?: TgWebApp };
  }
}

// Безпечний геттер — поза Telegram повертає null (для розробки в браузері без SDK)
export function getTg(): TgWebApp | null {
  return window.Telegram?.WebApp ?? null;
}

// initData для підпису API-запитів. Поза Telegram — пустий рядок (запити повернуть 401, що правильно).
export function getInitData(): string {
  return getTg()?.initData ?? '';
}

// Параметр що передається при відкритті Mini App з deep-link (наприклад, battle_ABC123).
// Telegram кладе його в одне з двох місць залежно від того, як відкрили додаток:
//   - initDataUnsafe.start_param: при t.me/<bot>?startapp=X або t.me/<bot>/<app>?startapp=X
//   - URL ?startapp=...: коли бот шле web_app кнопку з URL, що містить query (інлайн-клавіатура)
// Тому перевіряємо обидва.
export function getStartParam(): string | null {
  const fromSdk = getTg()?.initDataUnsafe.start_param;
  if (fromSdk) return fromSdk;
  try {
    return new URLSearchParams(window.location.search).get('startapp');
  } catch {
    return null;
  }
}

// Викликати один раз при старті — каже Telegram "я готовий, можна показувати".
// Також пушимо theme params у CSS-змінні, щоб Tailwind їх підхопив.
export function initTelegram() {
  const tg = getTg();
  if (!tg) return;
  tg.ready();
  tg.expand();
  applyTheme(tg);
  tg.onEvent('themeChanged', () => applyTheme(tg));
}

function applyTheme(tg: TgWebApp) {
  const root = document.documentElement;
  const t = tg.themeParams;
  const set = (cssVar: string, value: string | undefined) => {
    if (value) root.style.setProperty(cssVar, value);
  };
  set('--tg-bg', t.bg_color);
  set('--tg-secondary-bg', t.secondary_bg_color);
  set('--tg-text', t.text_color);
  set('--tg-hint', t.hint_color);
  set('--tg-link', t.link_color);
  set('--tg-button', t.button_color);
  set('--tg-button-text', t.button_text_color);
  set('--tg-destructive', t.destructive_text_color);
  // Колірна схема — для dark/light варіацій компонентів
  root.dataset.colorScheme = tg.colorScheme;
}

export const haptic = {
  light: () => getTg()?.HapticFeedback.impactOccurred('light'),
  medium: () => getTg()?.HapticFeedback.impactOccurred('medium'),
  success: () => getTg()?.HapticFeedback.notificationOccurred('success'),
  error: () => getTg()?.HapticFeedback.notificationOccurred('error'),
  warning: () => getTg()?.HapticFeedback.notificationOccurred('warning'),
  selection: () => getTg()?.HapticFeedback.selectionChanged(),
};

/**
 * Відкриває зовнішній URL з Mini App (наприклад, форму оплати WayForPay).
 *
 * Чому не звичайне `window.open()`: у Telegram WebView (особливо на iOS)
 * `window.open` поводиться непередбачувано, а POST-форма з target="_blank"
 * взагалі деградує до GET — і WayForPay показує "Bad Request — only POST data".
 *
 * Тому використовуємо нативний Telegram.WebApp.openLink — він гарантовано
 * відкриває URL у зовнішньому браузері (Safari/Chrome), де submit з autosubmit
 * сторінки спрацьовує штатно.
 */
export function openExternalLink(url: string) {
  const tg = getTg();
  if (tg?.openLink) {
    tg.openLink(url);
    return;
  }
  // Fallback для старих клієнтів Telegram або dev-режиму без SDK
  const w = window.open(url, '_blank', 'noopener,noreferrer');
  if (!w) window.location.href = url;
}
